import React, { useEffect, useRef, useState, useCallback } from 'react';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';

import Field, { hasDataInFields } from '../../Field';
import BlockBuilder from '../../BlockBuilder';

import SectionContainer from '../SectionContainer';
import css from './SectionColumns.module.css';

// Detailed content for the process step modals
const PROCESS_STEP_DETAILS = [
  {
    step: 1,
    title: 'Submit a Request',
    subtitle: 'Quick, free, and no obligation.',
    details: [
      { icon: '📋', text: 'Tell us what you need — tree work, brush clearing, defensible space, and more' },
      { icon: '📍', text: 'Share your property location and county so we can match you locally' },
      { icon: '🔒', text: 'Your information is kept private and only shared with matched service providers' },
      { icon: '⏱️', text: 'Takes less than 2 minutes to complete' },
    ],
    cta: 'Submit a request',
    ctaLink: '/request',
  },
  {
    step: 2,
    title: 'Connect with Local Professionals',
    subtitle: 'Local professionals in your area reach out.',
    details: [
      { icon: '🔍', text: 'We match you with providers who serve your county and specialize in your needs' },
      { icon: '✅', text: 'Verify any contractor’s CSLB license at cslb.ca.gov before hiring' },
      { icon: '💬', text: 'Matched contractors review your request and reach out with quotes' },
      { icon: '🤝', text: 'No commitment — compare options at your own pace' },
    ],
    cta: 'Browse contractors',
    ctaLink: '/vendors',
  },
  {
    step: 3,
    title: 'Choose a Provider & Start',
    subtitle: 'You\'re in control from start to finish.',
    details: [
      { icon: '⚖️', text: 'Compare quotes, reviews, and experience levels side by side' },
      { icon: '👤', text: 'Pick the provider that fits your budget and timeline' },
      { icon: '📞', text: 'Communicate directly with your chosen specialist' },
      { icon: '🛡️', text: 'Start building your defense against wildfire — on your terms' },
    ],
    cta: 'Get started',
    ctaLink: '/request',
  },
];

// The number of columns (numColumns) affects styling and responsive images
const COLUMN_CONFIG = [
  { css: css.oneColumn, responsiveImageSizes: '(max-width: 767px) 100vw, 1200px' },
  { css: css.twoColumns, responsiveImageSizes: '(max-width: 767px) 100vw, 600px' },
  { css: css.threeColumns, responsiveImageSizes: '(max-width: 767px) 100vw, 400px' },
  { css: css.fourColumns, responsiveImageSizes: '(max-width: 767px) 100vw, 265px' },
];
const getIndex = numColumns => numColumns - 1;
const getColumnCSS = numColumns => {
  const config = COLUMN_CONFIG[getIndex(numColumns)];
  return config ? config.css : COLUMN_CONFIG[0].css;
};
const getResponsiveImageSizes = numColumns => {
  const config = COLUMN_CONFIG[getIndex(numColumns)];
  return config ? config.responsiveImageSizes : COLUMN_CONFIG[0].responsiveImageSizes;
};

/**
 * @typedef {Object} BlockConfig
 * @property {string} blockId
 * @property {string} blockName
 * @property {'defaultBlock' | 'footerBlock' | 'socialMediaLink'} blockType
 */

/**
 * @typedef {Object} FieldComponentConfig
 * @property {ReactNode} component
 * @property {Function} pickValidProps
 */

/**
 * Section component that's able to show blocks in multiple different columns (defined by "numColumns" prop)
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.defaultClasses
 * @param {string} props.defaultClasses.sectionDetails
 * @param {string} props.defaultClasses.title
 * @param {string} props.defaultClasses.description
 * @param {string} props.defaultClasses.ctaButton
 * @param {string} props.sectionId id of the section
 * @param {'columns'} props.sectionType
 * @param {Object?} props.title
 * @param {Object?} props.description
 * @param {Object?} props.appearance
 * @param {Object?} props.callToAction
 * @param {Array<BlockConfig>?} props.blocks array of block configs
 * @param {boolean?} props.isInsideContainer
 * @param {Object} props.options extra options for the section component (e.g. custom fieldComponents)
 * @param {Object<string,FieldComponentConfig>?} props.options.fieldComponents custom fields
 * @returns {JSX.Element} Section for article content
 */
const SectionColumns = props => {
  const {
    sectionId,
    className,
    rootClassName,
    defaultClasses,
    numColumns,
    title,
    description,
    appearance,
    callToAction,
    blocks = [],
    isInsideContainer = false,
    options,
  } = props;

  const containerRef = useRef(null);
  const [blocksVisible, setBlocksVisible] = useState(false);
  const [activeStep, setActiveStep] = useState(null);
  const history = useHistory();

  const isProcessSection = numColumns === 3;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setBlocksVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Close modal on Escape key
  useEffect(() => {
    if (activeStep === null) return;
    const handleKeyDown = e => {
      if (e.key === 'Escape') setActiveStep(null);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeStep]);

  // Handle card click for process steps
  const handleCardClick = useCallback((e, index) => {
    if (!isProcessSection) return;
    // Don't intercept clicks on links/buttons inside blocks
    if (e.target.closest('a') || e.target.closest('button')) return;
    setActiveStep(index);
  }, [isProcessSection]);

  const handleCtaClick = useCallback((link) => {
    setActiveStep(null);
    history.push(link);
  }, [history]);

  // If external mapping has been included for fields
  // E.g. { h1: { component: MyAwesomeHeader } }
  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };

  const hasHeaderFields = hasDataInFields([title, description, callToAction], fieldOptions);
  const hasBlocks = blocks?.length > 0;

  return (
    <SectionContainer
      id={sectionId}
      className={className}
      rootClassName={rootClassName}
      appearance={appearance}
      options={fieldOptions}
    >
      {hasHeaderFields ? (
        <header className={defaultClasses.sectionDetails}>
          <Field data={title} className={defaultClasses.title} options={fieldOptions} />
          <Field data={description} className={defaultClasses.description} options={fieldOptions} />
          <Field data={callToAction} className={defaultClasses.ctaButton} options={fieldOptions} />
        </header>
      ) : null}
      {hasBlocks ? (
        <div
          ref={containerRef}
          className={classNames(defaultClasses.blockContainer, getColumnCSS(numColumns), {
            [css.noSidePaddings]: isInsideContainer,
            [css.blocksVisible]: blocksVisible,
            [css.clickableCards]: isProcessSection,
          })}
          onClick={isProcessSection ? (e) => {
            if (e.target.closest('a') || e.target.closest('button')) return;
            // Walk up from the click target to find which direct child card was clicked
            const container = containerRef.current;
            let node = e.target;
            while (node && node.parentElement !== container) {
              node = node.parentElement;
            }
            if (!node || node.parentElement !== container) return;
            const index = Array.from(container.children).indexOf(node);
            if (index >= 0) setActiveStep(index);
          } : undefined}
        >
          <BlockBuilder
            ctaButtonClass={defaultClasses.ctaButton}
            blocks={blocks}
            sectionId={sectionId}
            responsiveImageSizes={getResponsiveImageSizes(numColumns)}
            options={options}
          />
        </div>
      ) : null}

      {/* Process step detail modal */}
      {activeStep !== null && PROCESS_STEP_DETAILS[activeStep] && (
        <div className={css.modalOverlay} onClick={() => setActiveStep(null)}>
          <div className={css.modalContent} onClick={e => e.stopPropagation()}>
            <button
              className={css.modalClose}
              onClick={() => setActiveStep(null)}
              aria-label="Close"
            >
              &times;
            </button>
            <div className={css.modalStep}>
              Step {PROCESS_STEP_DETAILS[activeStep].step}
            </div>
            <h3 className={css.modalTitle}>
              {PROCESS_STEP_DETAILS[activeStep].title}
            </h3>
            <p className={css.modalSubtitle}>
              {PROCESS_STEP_DETAILS[activeStep].subtitle}
            </p>
            <ul className={css.modalDetails}>
              {PROCESS_STEP_DETAILS[activeStep].details.map((item, i) => (
                <li key={i} className={css.modalDetailItem}>
                  <span className={css.modalDetailIcon}>{item.icon}</span>
                  <span>{item.text}</span>
                </li>
              ))}
            </ul>
            <button
              className={css.modalCta}
              onClick={() => handleCtaClick(PROCESS_STEP_DETAILS[activeStep].ctaLink)}
            >
              {PROCESS_STEP_DETAILS[activeStep].cta}
            </button>
          </div>
        </div>
      )}
    </SectionContainer>
  );
};

export default SectionColumns;
